import { EmptyState, ErrorNote, Spinner } from '../ui/primitives.js';
// Lexical search over readable segments and summaries. Snippets are plain
// text (React text nodes), never HTML. Segment hits on the meeting timeline
// open the meeting with that segment selected (`?t=`); track-relative ones
// open it plainly and say their time is the microphone's own.
import { useState } from 'react';
import type { FormEvent } from 'react';
import type { WebSearchHit } from '../api/types.js';
import { COPY } from '../copy.js';
import { Link, paths } from '../router.js';
import { useServices } from '../services.js';
import { formatClock, formatDate, speakerLabel } from '../ui/labels.js';
import { usePaged } from './usePaged.js';

export const SEARCH_MAX_CHARS = 200;

export function Search() {
  const { api } = useServices();
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState<string | null>(null);
  const results = usePaged<WebSearchHit>(query, (cursor, signal) => api.search(query ?? '', { cursor }, signal));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = draft.trim();
    setQuery(q.length > 0 ? q : null);
  };

  return (
    <section aria-labelledby="search-title">
      <h2 id="search-title">{COPY.search}</h2>
      <form role="search" onSubmit={submit} className="search">
        <input
          type="search"
          aria-label={COPY.searchLabel}
          placeholder={COPY.searchLabel}
          value={draft}
          maxLength={SEARCH_MAX_CHARS}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button type="submit" className="search__submit">{COPY.search}</button>
      </form>
      {query !== null && !results.loading && !results.error && results.items.length === 0 && (
        <EmptyState title={COPY.searchEmpty} />
      )}
      <ul className="list" data-testid="search-results">
        {results.items.map((hit, i) => (
          <li key={`${hit.meeting_id}:${hit.segment_id ?? 'summary'}:${i}`}>
            <Link href={paths.meeting(hit.meeting_id, hit.timeline === 'meeting' ? hit.start_ms : null)}>
              {hit.room_slug ? `${hit.room_slug} \u00b7 ` : ''}
              {formatDate(hit.meeting_started_at)}
            </Link>{' '}
            <span className="muted">
              {hit.kind === 'summary'
                ? COPY.summaryHit
                : `${speakerLabel(hit.speaker_kind, hit.speaker)}${hit.start_ms !== null ? ` \u00b7 ${formatClock(hit.start_ms)}` : ''}${hit.timeline === 'track' ? ` · ${COPY.trackRelative}` : ''}`}
            </span>
            <p className="snippet">{hit.snippet}</p>
          </li>
        ))}
      </ul>
      {results.loading && <div className="loading"><Spinner /><span>{COPY.loading}</span></div>}
      {results.error && <ErrorNote message={results.error} />}
      {results.hasMore && !results.loading && (
        <button type="button" onClick={results.loadMore}>
          {COPY.loadMore}
        </button>
      )}
    </section>
  );
}
